import React, { useEffect, useState, useCallback, useMemo } from "react";
import { BookOpen, TrendingUp, Target, Gauge, Layers, ArrowUpRight, ArrowDownRight, Camera, Sparkles, Check, Trash2, Activity, ChevronRight } from "lucide-react";
import { journalEntries, journalStats, weeklyReview } from "../mock";
import ChartReplay from "../components/ChartReplay";
import ConnectMT5 from "../components/ConnectMT5";
import PositionWSView from "../components/PositionWSView";
import TradeDetail from "../components/TradeDetail";
import { getClientId } from "../utils/clientId";
import { groupCompletedTrades } from "../utils/mt5Trades";
import { fmtLocalShort, fmtLocal } from "../utils/time";
import { useAuth } from "../context/AuthContext";
import { List } from "react-window";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MT5_ROW_H = 84; // fixed row height (incl. 12px gap) for the virtualized list

// One virtualized Recent-Entry row (react-window passes index + style + rowProps).
const Mt5CompletedRow = ({ index, style, items, onOpen }) => {
  const t = items[index];
  const buy = String(t.side || "").toLowerCase().includes("buy") || t.side === 0;
  const profit = t.realized;
  return (
    <div style={style}>
      <div className="pb-3 h-full">
        <div onClick={() => onOpen(t.key)} className="ws-price-cell ws-clickable p-4 h-full flex items-center gap-4 cursor-pointer" style={{ borderColor: "rgba(56,189,248,0.4)" }} data-testid={`mt5-completed-${t.key}`} title="View full trade breakdown & AI review">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${buy ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
            {buy ? <ArrowUpRight className="w-4 h-4 ws-up" /> : <ArrowDownRight className="w-4 h-4 ws-down" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="ws-heading font-semibold text-white text-[15px]">{t.symbol || "—"}</span>
              <span className={`text-[11px] font-semibold ${buy ? "ws-up" : "ws-down"}`}>{buy ? "BUY" : "SELL"}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(56,189,248,0.18)] text-[color:var(--ws-cyan)]">MT5 / Imported</span>
              <span className="text-[11px] text-[color:var(--ws-muted)]">{t.volume != null ? `${t.volume} lot` : ""}</span>
            </div>
            <p className="text-[12px] text-[color:var(--ws-muted)] truncate mt-0.5">
              Position #{t.key}{t.count > 1 ? ` · ${t.count} fills` : ""}{(t.ts || t.time) ? ` · ${fmtLocalShort(t.ts || t.time)}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className={`ws-heading font-semibold text-[14px] ${profit >= 0 ? "ws-up" : "ws-down"}`}>
              {profit >= 0 ? "+" : ""}{profit}
            </div>
            <ChevronRight className="w-4 h-4 text-[color:var(--ws-muted)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

const stat = [
  { label: "Win Rate", value: `${journalStats.winRate}%`, icon: Target },
  { label: "Discipline Score", value: `${journalStats.disciplineScore}`, icon: Gauge },
  { label: "Total Trades", value: journalStats.totalTrades, icon: Layers },
  { label: "Net P&L", value: journalStats.netPnl, icon: TrendingUp },
];

const resultColor = (r) => (r === "Win" ? "ws-up" : r === "Loss" ? "ws-down" : "text-[color:var(--ws-muted)]");

const AIJournal = () => {
  const clientId = getClientId();
  const { user } = useAuth();
  const [logged, setLogged] = useState([]);
  const [stats, setStats] = useState(null);
  const [mt5, setMt5] = useState([]);
  const [mt5Stats, setMt5Stats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [rangeDays, setRangeDays] = useState(0); // 0 = All
  const [wsViewTicket, setWsViewTicket] = useState(null);
  const [detailPid, setDetailPid] = useState(null);
  const prevTicketsRef = React.useRef(null);
  const loadMt5Ref = React.useRef(null);

  // WS Coins: award journaling time while this page is open & the tab is visible (~1 coin batch/min).
  useEffect(() => {
    if (!user) return;
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      fetch(`${API}/growth/journal-heartbeat`, { method: "POST", credentials: "include" }).catch(() => {});
    };
    const t = setInterval(beat, 60000);
    return () => clearInterval(t);
  }, [user]);


  // Lightweight, dedicated fetch of MT5 imported trades + stats (Recent Entries data).
  const loadMt5 = useCallback(async () => {
    try {
      const qs = rangeDays ? `?days=${rangeDays}` : "";
      const [mr, msr] = await Promise.all([
        fetch(`${API}/mt5/trades${qs}`, { credentials: "include" }),
        fetch(`${API}/mt5/stats${qs}`, { credentials: "include" }),
      ]);
      if (mr.ok) setMt5((await mr.json()).trades || []);
      if (msr.ok) setMt5Stats(await msr.json());
    } catch { /* keep last known */ }
  }, [rangeDays]);
  useEffect(() => { loadMt5Ref.current = loadMt5; }, [loadMt5]);

  // Lightweight, dedicated poll for LIVE open positions (real MT5 data) — every 1s.
  // When the open-position set CHANGES at all (a position closed OR a new one opened),
  // immediately refresh Recent Entries so new MT5 trades appear without a manual refresh.
  const loadPositions = useCallback(async () => {
    try {
      const pr = await fetch(`${API}/mt5/positions`, { credentials: "include" });
      const next = pr.ok ? ((await pr.json()).positions || []) : [];
      const nextTickets = new Set(next.map((p) => String(p.ticket)));
      const prev = prevTicketsRef.current;
      const changed = prev && (prev.size !== nextTickets.size ||
        [...nextTickets].some((tk) => !prev.has(tk)) || [...prev].some((tk) => !nextTickets.has(tk)));
      if (changed) loadMt5Ref.current && loadMt5Ref.current();
      prevTicketsRef.current = nextTickets;
      setPositions(next);
    } catch { /* keep last known */ }
  }, []);
  useEffect(() => {
    loadPositions();
    const t = setInterval(loadPositions, 1000);
    return () => clearInterval(t);
  }, [loadPositions, user]);

  // Near-real-time fallback: refresh Recent Entries every 5s so trades opened AND closed
  // between position ticks (never seen as "open") still surface automatically.
  useEffect(() => {
    const t = setInterval(() => loadMt5Ref.current && loadMt5Ref.current(), 5000);
    return () => clearInterval(t);
  }, [user]);

  const loadLogged = useCallback(async () => {
    try {
      const [jr, sr] = await Promise.all([
        fetch(`${API}/journal?client_id=${clientId}`, { credentials: "include" }),
        fetch(`${API}/journal-stats?client_id=${clientId}`, { credentials: "include" }),
      ]);
      const jd = await jr.json();
      const sd = await sr.json();
      setLogged(jd.entries || []);
      setStats(sd);
    } catch {}
    await loadMt5();
  }, [clientId, loadMt5]);
  // Reload when auth or selected range changes.
  useEffect(() => { loadLogged(); }, [user, loadLogged]); // eslint-disable-line

  const removeEntry = async (id) => {
    try {
      await fetch(`${API}/journal/${id}?client_id=${clientId}`, { method: "DELETE", credentials: "include" });
      setLogged((prev) => prev.filter((e) => e.id !== id));
      loadLogged();
    } catch {}
  };

  const fmtDate = (iso) => fmtLocalShort(iso);

  // REAL data only. MT5 stats when connected & has data; else logged-trade stats;
  // else a genuine zero-state (no fabricated demo figures).
  const hasReal = stats && stats.total > 0;
  const hasMt5 = mt5Stats && mt5Stats.has_data;
  const pf = (v) => (v == null ? "—" : v >= 999 ? "∞" : v);
  const displayStats = hasMt5
    ? [
        { label: "Win Rate", value: mt5Stats.win_rate != null ? `${mt5Stats.win_rate}%` : "—", icon: Target },
        { label: "Total Trades", value: mt5Stats.total_trades, icon: Layers },
        { label: "Net Profit", value: `${mt5Stats.net_profit >= 0 ? "+" : ""}${mt5Stats.net_profit}`, icon: TrendingUp },
        { label: "Profit Factor", value: pf(mt5Stats.profit_factor), icon: Gauge },
      ]
    : hasReal
    ? [
        { label: "Win Rate", value: stats.win_rate != null ? `${stats.win_rate}%` : "—", icon: Target },
        { label: "Avg R:R", value: stats.avg_rr != null ? `1:${stats.avg_rr}` : "—", icon: Gauge },
        { label: "Trades Logged", value: stats.total, icon: Layers },
        { label: "Net (pts)", value: stats.net_pnl != null ? `${stats.net_pnl > 0 ? "+" : ""}${stats.net_pnl}` : "—", icon: TrendingUp },
      ]
    : [
        { label: "Win Rate", value: "—", icon: Target },
        { label: "Profit Factor", value: "—", icon: Gauge },
        { label: "Total Trades", value: 0, icon: Layers },
        { label: "Net Profit", value: "0", icon: TrendingUp },
      ];

  // Group raw MT5 executions into COMPLETED trades (by position_id + entry), so an
  // opening/entry deal never shows as its own +0 row. Mirrors backend _completed_trades.
  // Still-open positions are excluded here so they only appear under Open Positions.
  const openTickets = useMemo(() => new Set(positions.map((p) => String(p.ticket))), [positions]);
  const mt5Completed = useMemo(
    () => groupCompletedTrades(mt5).filter((g) => !openTickets.has(String(g.key))),
    [mt5, openTickets]
  );

  const totalRunningPnl = useMemo(
    () => positions.reduce((sum, p) => sum + (Number(p.profit) || 0), 0),
    [positions]
  );
  const selectedPosition = useMemo(
    () => positions.find((p) => String(p.ticket) === String(wsViewTicket)) || null,
    [positions, wsViewTicket]
  );

  return (
  <div className="max-w-[1200px] mx-auto px-8 py-8">
    <div className="flex items-center gap-3 mb-2 ws-fade-up">
      <div className="ws-icon-chip w-9 h-9"><BookOpen className="w-5 h-5" /></div>
      <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Your Trades, Understood</span>
    </div>
    <h1 className="ws-heading text-[40px] font-bold mb-6 ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>AI Journal</h1>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ws-fade-up" style={{ animationDelay: "80ms" }}>
      {displayStats.map((s) => (
        <div key={s.label} className="ws-panel ws-panel-hover p-5">
          <div className="ws-icon-chip w-10 h-10 mb-3"><s.icon className="w-5 h-5" /></div>
          <div className="ws-heading text-[26px] font-bold text-white">{s.value}</div>
          <div className="text-[12px] text-[color:var(--ws-muted)]">{s.label}</div>
        </div>
      ))}
    </div>

    <div className="mb-6 ws-fade-up" style={{ animationDelay: "100ms" }}>
      <ConnectMT5 onSynced={loadLogged} rangeDays={rangeDays} setRangeDays={setRangeDays} />
    </div>

    {positions.length > 0 && (
      <div className="ws-panel p-6 mb-6 ws-fade-up" style={{ animationDelay: "105ms" }} data-testid="mt5-open-positions">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="ws-icon-chip w-8 h-8"><Activity className="w-4 h-4" /></span>
            <h3 className="ws-heading text-[16px] font-semibold">Open Positions</h3>
            <span className="ws-live-dot" />
            <span className="text-[11px] text-[color:var(--ws-muted)]">live · updates every 1s · {positions.length} running</span>
          </div>
          <div className="ws-price-cell px-4 py-2 flex items-center gap-3" data-testid="total-running-pnl">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-muted)]">Total Running P&amp;L</span>
            <span className={`ws-heading text-[22px] font-bold tabular-nums ${totalRunningPnl >= 0 ? "ws-up" : "ws-down"}`}>
              {totalRunningPnl >= 0 ? "+" : ""}{totalRunningPnl.toFixed(2)}
            </span>
          </div>
        </div>
        <div className="space-y-3">
          {positions.map((p) => {
            const buy = String(p.type ?? "").toLowerCase().includes("buy") || p.type === 0 || p.type === "0";
            const profit = typeof p.profit === "number" ? p.profit : (p.profit != null ? Number(p.profit) : null);
            const open = p.price_open == null ? NaN : Number(p.price_open);
            const cur = p.price_current == null ? NaN : Number(p.price_current);
            const hasPx = !Number.isNaN(open) && !Number.isNaN(cur);
            const diff = hasPx ? cur - open : null;
            const diffPct = hasPx && open ? (diff / open) * 100 : null;
            // Favourable move = price up for BUY, price down for SELL.
            const fav = diff == null ? true : (buy ? diff >= 0 : diff <= 0);
            return (
              <div key={p.ticket || `${p.symbol}-${p.price_open}`} className="ws-price-cell p-4 flex items-center gap-4" style={{ borderColor: "rgba(52,211,153,0.4)" }} data-testid={`mt5-position-${p.ticket}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${buy ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
                  {buy ? <ArrowUpRight className="w-4 h-4 ws-up" /> : <ArrowDownRight className="w-4 h-4 ws-down" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="ws-heading font-semibold text-white text-[15px]">{p.symbol || "—"}</span>
                    <span className={`text-[11px] font-semibold ${buy ? "ws-up" : "ws-down"}`}>{buy ? "BUY" : "SELL"}</span>
                    {p.pending_close ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(251,191,36,0.16)] text-[#fbbf24]" title="Opening deal received; awaiting the broker's close/live data">AWAITING BROKER SYNC</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(52,211,153,0.18)] text-[color:var(--ws-green)]">OPEN</span>
                    )}
                    <span className="text-[11px] text-[color:var(--ws-muted)]">{p.volume != null ? `${p.volume} lot` : ""}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-[12px] mt-1 tabular-nums">
                    {p.price_open != null && (
                      <span className="text-[color:var(--ws-muted)]">Entry <span className="text-white">{p.price_open}</span></span>
                    )}
                    {hasPx && (
                      <>
                        <span className="text-[color:var(--ws-blue-bright)]">→</span>
                        <span className="text-[color:var(--ws-muted)]">Now <span className="text-white">{p.price_current}</span></span>
                        {diff != null && (
                          <span className={`font-semibold ${fav ? "ws-up" : "ws-down"}`}>
                            {diff >= 0 ? "+" : ""}{diff.toFixed(Math.abs(open) >= 100 ? 2 : 5)}
                            {diffPct != null ? ` (${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(2)}%)` : ""}
                          </span>
                        )}
                      </>
                    )}
                    {p.pending_close && <span className="text-[11px] text-[#fbbf24]">P&amp;L pending broker data</span>}
                    {p.sl != null && p.sl !== 0 ? <span className="text-[color:var(--ws-muted)]">SL {p.sl}</span> : null}
                    {p.tp != null && p.tp !== 0 ? <span className="text-[color:var(--ws-muted)]">TP {p.tp}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {profit != null && !Number.isNaN(profit) && (
                    <div className="text-right">
                      <div className={`ws-heading font-semibold text-[15px] tabular-nums ${profit >= 0 ? "ws-up" : "ws-down"}`}>{profit >= 0 ? "+" : ""}{profit.toFixed(2)}</div>
                      <div className="text-[10px] text-[color:var(--ws-muted)]">running P&amp;L</div>
                    </div>
                  )}
                  <button
                    onClick={() => setWsViewTicket(p.ticket)}
                    disabled={p.pending_close}
                    className={`px-3 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 shrink-0 ${p.pending_close ? "ws-btn-ghost opacity-40 cursor-not-allowed" : "ws-btn-primary"}`}
                    data-testid={`ask-ws-view-${p.ticket}`}
                    title={p.pending_close ? "Live price not yet available for this position" : "AI analysis of this live position"}
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Ask WS View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {selectedPosition && (
      <PositionWSView position={selectedPosition} onClose={() => setWsViewTicket(null)} />
    )}

    {detailPid && (
      <TradeDetail positionId={detailPid} onClose={() => setDetailPid(null)} />
    )}

    {hasMt5 && (
      <div className="ws-panel p-6 mb-6 ws-fade-up" style={{ animationDelay: "110ms" }}>
        <div className="flex items-center gap-2 mb-4">
          <span className="ws-icon-chip w-8 h-8"><Gauge className="w-4 h-4" /></span>
          <h3 className="ws-heading text-[16px] font-semibold">MT5 Performance</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(56,189,248,0.18)] text-[color:var(--ws-cyan)]">Verified MT5 facts</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["Gross Profit", `+${mt5Stats.gross_profit}`, "ws-up"],
            ["Gross Loss", `-${mt5Stats.gross_loss}`, "ws-down"],
            ["Avg Win", `+${mt5Stats.avg_win}`, "ws-up"],
            ["Avg Loss", `${mt5Stats.avg_loss}`, "ws-down"],
            ["Best Trade", mt5Stats.best_trade != null ? `${mt5Stats.best_trade >= 0 ? "+" : ""}${mt5Stats.best_trade}` : "—", "ws-up"],
            ["Worst Trade", mt5Stats.worst_trade != null ? `${mt5Stats.worst_trade}` : "—", "ws-down"],
          ].map(([k, v, cls]) => (
            <div key={k} className="ws-price-cell p-3">
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--ws-muted)]">{k}</div>
              <div className={`ws-heading text-[15px] font-bold ${cls}`}>{v}</div>
            </div>
          ))}
        </div>
        {mt5Stats.by_symbol?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--ws-muted)] mb-2">Net by Symbol</div>
            <div className="flex flex-wrap gap-2">
              {mt5Stats.by_symbol.map((s) => (
                <span key={s.symbol} className="ws-price-cell px-3 py-1.5 text-[12px]">
                  <span className="text-white font-semibold">{s.symbol}</span>{" "}
                  <span className={s.net >= 0 ? "ws-up" : "ws-down"}>{s.net >= 0 ? "+" : ""}{s.net}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}

    <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "120ms" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="ws-heading text-[17px] font-semibold">Recent Entries{mt5Completed.length > 0 ? <span className="text-[12px] text-[color:var(--ws-muted)] font-normal ml-2">{mt5Completed.length} closed</span> : null}</h3>
        <button className="ws-btn-ghost px-3 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2"><Camera className="w-4 h-4" /> Snap-to-Journal</button>
      </div>
      {mt5Completed.length > 0 && (
        <div className="mb-3" style={{ height: Math.min(mt5Completed.length, 7) * MT5_ROW_H }} data-testid="mt5-completed-list">
          <List
            rowComponent={Mt5CompletedRow}
            rowCount={mt5Completed.length}
            rowHeight={MT5_ROW_H}
            rowProps={{ items: mt5Completed, onOpen: setDetailPid }}
            style={{ height: "100%", width: "100%" }}
          />
        </div>
      )}
      <div className="space-y-3">
        {logged.map((e) => {
          const isLong = (e.side || "Long") === "Long";
          const hasLevels = e.entry != null && e.exit != null;
          return (
          <div key={e.id} className="ws-price-cell p-4 flex items-center gap-4" style={{ borderColor: "rgba(96,165,250,0.35)" }}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isLong ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
              {isLong ? <ArrowUpRight className="w-4 h-4 ws-up" /> : <ArrowDownRight className="w-4 h-4 ws-down" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="ws-heading font-semibold text-white text-[15px]">{e.symbol}</span>
                <span className={`text-[11px] font-semibold ${isLong ? "ws-up" : "ws-down"}`}>{e.side}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(59,130,246,0.16)] text-[color:var(--ws-blue-bright)]">from chart</span>
                <span className="text-[11px] text-[color:var(--ws-muted)]">{e.interval} · {fmtDate(e.created_at)}</span>
              </div>
              <p className="text-[12px] text-[color:var(--ws-muted)] truncate mt-0.5">{e.note}</p>
            </div>
            <div className="text-right shrink-0 mr-1">
              {hasLevels && (
                <div className="text-[11px] text-[color:var(--ws-muted)] tabular-nums">
                  {e.entry} <span className="text-[color:var(--ws-blue-bright)]">→</span> {e.exit}
                </div>
              )}
              {e.rr != null && (
                <div className={`ws-heading font-semibold text-[13px] ${e.rr >= 1 ? "ws-up" : "ws-down"}`}>1 : {e.rr}</div>
              )}
            </div>
            <button onClick={() => removeEntry(e.id)} className="text-[color:var(--ws-muted)] hover:text-[color:var(--ws-red)] transition-colors shrink-0" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          );
        })}
        {mt5Completed.length === 0 && logged.length === 0 && (
          <div className="ws-price-cell p-8 text-center">
            <p className="text-[14px] text-white font-medium mb-1">No trades yet</p>
            <p className="text-[13px] text-[color:var(--ws-muted)]">Connect your MT5 account above, or log a trade from the WS Chart. Real trades appear here automatically.</p>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default AIJournal;
